import {
  useTableFilter,
  useTableServerSideFiltering,
  sanitizeTodos, cellNotFilter,
  formatDateWithTimeAMPM, emptyCell,
  emptyCellObject,
} from "@/shared";
import { useFetchMovimientoInventario } from "@/shared/actions/erp";
import { useExcelDownloadLoaderStore } from "../componente/useExcelDownloadLoader.store";
import { IDataMovimientoInventario, KardexPorBodegaFilter } from "@/shared/interfaces";
import { MRT_ColumnDef } from "material-react-table";
import { useState, useRef, useMemo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import Flatpickr from 'react-flatpickr';
import { ChipModelState } from "@/shared/components/ui";
import { Button } from "@mui/material";
import { useModalKardexSeriesStore } from "./useModalKardexSeriesStore.store";
import InfoUser from "@/shared/components/ui/InfoUser/InfoUser";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useUiStore } from "@/store/ui";
import dayjs from "dayjs";

type ResultadoMovimiento = {
  totalEntrada: string;
  totalSalida: string;
  stock: string;
};

enum TipoMovimientoENUM {
  INGRESO = 'INGRESO',
  TRASLADO = 'TRASLADO',
  EGRESO = 'EGRESO'
}

const TIPO_MOVIMIENTO_CHOICE = [
  TipoMovimientoENUM.INGRESO,
  TipoMovimientoENUM.TRASLADO,
  TipoMovimientoENUM.EGRESO,
];

function handleColorTipo(tipo: string) {
  const color: Record<string, string> = {
    'INGRESO': 'success',
    'TRASLADO': 'info',
    'EGRESO': 'error',
  }
  return color[tipo] ?? 'info'
}

export function handleTipoMovimiento(
  data: IDataMovimientoInventario,
  bodegaFilter: string | null
): ResultadoMovimiento {
  const {
    tipo,
    cantidad_metros,
    cantidad,
    stockhistorico_origen_reverso,
    stockhistorico_destino_reverso,
    metroshistoricos_destino_reverso,
    metroshistoricos_origen_reverso,
    producto_data,
    bodega_origen,
  } = data;

  const usaMetros = producto_data?.usa_metros ?? false;
  const DASH = "—";

  // Formatea cantidad ya sea en unidades o metros
  function formatCantidad(
    cantUnidad?: string | number | null,
    cantMetros?: string | number | null
  ): string {
    const unidad = cantUnidad != null ? String(cantUnidad) : "0";
    const metros = cantMetros != null ? String(cantMetros) : "0";

    // Si no hay nada en ninguna, puedes devolver "—" si prefieres
    if (cantUnidad == null && cantMetros == null) {
      return DASH;
    }

    return usaMetros ? `${metros} (METROS)` : unidad;
  }

  const esOrigen = bodegaFilter === bodega_origen;

  // Stock se calcula igual en todos los casos, solo cambia origen/destino
  const stock = esOrigen
    ? formatCantidad(
      stockhistorico_origen_reverso,
      metroshistoricos_origen_reverso
    )
    : formatCantidad(
      stockhistorico_destino_reverso,
      metroshistoricos_destino_reverso
    );

  let totalEntrada = DASH;
  let totalSalida = DASH;

  switch (tipo) {
    case TipoMovimientoENUM.INGRESO: {
      totalEntrada = formatCantidad(cantidad, cantidad_metros);
      break;
    }

    case TipoMovimientoENUM.TRASLADO: {
      const valorMovimiento = formatCantidad(cantidad, cantidad_metros);

      if (esOrigen) {
        totalSalida = valorMovimiento;
      } else {
        totalEntrada = valorMovimiento;
      }
      break;
    }

    case TipoMovimientoENUM.EGRESO: {
      totalSalida = formatCantidad(cantidad, cantidad_metros);
      break;
    }

    default:
      // se mantienen los "—"
      break;
  }

  return {
    totalEntrada,
    totalSalida,
    stock,
  };
}

function numReferenciaTipo(data: IDataMovimientoInventario): string | null {

  const {
    ingreso_inventario,
    egreso_inventario,
    traslado_inventario
  } = data

  if (ingreso_inventario)
    return ingreso_inventario
  if (egreso_inventario)
    return egreso_inventario
  if (traslado_inventario)
    return traslado_inventario

  return '—'
}

export default function useKardexByBodega() {

  ///* form
  const form = useForm<KardexPorBodegaFilter>({
    defaultValues: {
      bodega_origen_o_destino: null,
      ubicacion_origen_o_destino: null,
      fecha_creacion_after: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
      fecha_creacion_before: dayjs().format('YYYY-MM-DD')
    }
  });


  const [bodega, ubicacion,
    producto, descripcion, series] = form.watch([
      'bodega_origen_o_destino',
      'ubicacion_origen_o_destino',
      'producto',
      'descripcion_producto',
      'serie'
    ]);

  const [enabled, setEnabled] = useState<boolean>(false);
  const flatpickrRef = useRef<Flatpickr>(null);

  const {
    pagination,
    setPagination,
  } = useTableFilter();

  const {
    filterObject,
    setFilterObject,
    columnFilters,
    setColumnFilters
  } =
    useTableServerSideFiltering();

  const {
    pageIndex,
    pageSize
  } = pagination;


  ///* fetch
  const cleanedFilters = useMemo(
    () => ({
      bodega_origen_o_destino: sanitizeTodos(bodega ?? null),
      ubicacion_origen_o_destino: sanitizeTodos(ubicacion ?? null),
      producto: sanitizeTodos(producto ?? null),
      descripcion_producto: sanitizeTodos(descripcion ?? null),
      serie: sanitizeTodos(series ?? null),
    }),
    [bodega, ubicacion, producto, descripcion, series],
  );

  const {
    data: dataKardex,
    isLoading: isLoadingKardex,
    isRefetching: isRefetchingKardex,
    refetch
  } = useFetchMovimientoInventario({
    params: {
      ...form.getValues(),
      ...cleanedFilters,
      page: pageIndex + 1,
      page_size: pageSize,
      ...filterObject,
      filterByState: false
    },
    enabled: enabled
  });


  const setOpen = useModalKardexSeriesStore(s => s.setOpen);
  const setSeries = useModalKardexSeriesStore(s => s.setSeries);
  //const setParamsKardex = useKardexStore(s => s.setParamsKardex);

  useEffect(() => {
    if (dataKardex?.data.count === 0) {
      toast.info('Sin movimientos.')
    }
    return () => {
    }
  }, [isLoadingKardex])



  const columnBase = useMemo<MRT_ColumnDef<IDataMovimientoInventario>[]>(
    () => [
      {
        id: 'detalle',
        header: 'Detalles',
        columns: [
          {
            ...cellNotFilter,
            accessorKey: 'fecha_documento',
            header: 'Fecha',
            size: 200,
            Cell: ({ row }) => (
              `${formatDateWithTimeAMPM(row.original.fecha_creacion)} - ${formatDateWithTimeAMPM(row.original.fecha_modificacion)}`
            ),
          },
          {
            accessorKey: 'numero_referencia',
            header: '# Movimiento Kardex',
            size: 125,
          },
          {
            id: 'codigo_referencia_tipo',
            header: '# Referencia Tipo',
            accessorKey: 'codigo_referencia_tipo',
            size: 125,
            Cell: ({ row }) => numReferenciaTipo(row.original)
          },
          {
            header: 'Usuario',
            accessorKey: 'user',
            Cell: ({ row }) => (
              <InfoUser user={row.original.user!}
              />
            )
          },
          {
            accessorKey: 'producto',
            header: 'Cod. Producto',
          },
          {
            ...cellNotFilter,
            accessorKey: 'series',
            header: 'series',
            Cell: ({ row }) => <Button
              disabled={!row.original.series.length}
              startIcon={<OpenInNewIcon />}
              onClick={() => {

                const base = row.original

                const params = {
                  producto: base.producto ?? '',
                  movimiento_inventario: base.codigo ?? ''
                }

                setOpen(true)
                setSeries(base.series ?? [], params)
              }}
            >VER SERIES</Button>
          },
          {
            //...cellNotFilter,
            id: 'descripcion_producto',
            accessorKey: 'producto_data.descripcion',
            header: 'Descripción',
          },
          {
            accessorKey: 'tipo',
            filterSelectOptions: TIPO_MOVIMIENTO_CHOICE,
            filterVariant: 'select',
            header: 'Tipo Movimiento',
            size: 125,
            Cell: ({ row }) => {
              return <ChipModelState
                label={row.original.tipo ?? 'N/A'}
                //color="error"
                color={handleColorTipo(row.original.tipo ?? '') as any}
              />
            }
          },
          {
            ...cellNotFilter,
            accessorKey: 'motivo_nombre',
            header: 'Motivo',
            Cell: ({ row }) => emptyCellObject(row.original.motivo_data, 'nombre')
          },
        ]
      },
      {
        id: 'detalle_entrada',
        header: 'Entradas',
        columns: [
          {
            ...cellNotFilter,
            accessorKey: 'total_entradas',
            header: 'Total', //'—'
            size: 50,
            Cell: ({ row }) => {
              return handleTipoMovimiento(row.original,
                form.getValues().bodega_origen_o_destino)?.totalEntrada
            }
          },
        ]
      },
      {
        id: 'detalle_salidas',
        header: 'Salidas',
        columns: [
          {
            ...cellNotFilter,
            accessorKey: 'total_salidas',
            header: 'Total',
            size: 50,
            Cell: ({ row }) => {
              return handleTipoMovimiento(row.original,
                form.getValues().bodega_origen_o_destino)?.totalSalida
            }
          },
        ]
      },
      {
        id: 'detalle_existencia',
        header: 'existencias',
        columns: [
          {
            ...cellNotFilter,
            accessorKey: 'existencias',
            header: 'Total',
            size: 50,
            Cell: ({ row }) => handleTipoMovimiento(row.original,
              form.getValues().bodega_origen_o_destino ?? 'none'
            ).stock

          },
        ]
      },
      {
        id: 'detalle_valores',
        header: 'Valores',
        columns: [
          {
            ...cellNotFilter,
            header: 'Costo Unitario',
            accessorKey: 'costo_actual',
            size: 75,
            Cell: ({ row }) => emptyCell(row.original?.producto_data?.costo_actual, '—')
          },
          {
            ...cellNotFilter,
            header: 'Total Valorizado',
            accessorKey: 'total_valorizado',
            size: 75,
            Cell: ({ row }) => emptyCell(row.original?.total_valorizado, '—')
          },
        ]
      },
      {
        id: 'otros',
        header: 'Otros',
        columns: [
          {
            id: 'bodega_origen_nombre',
            header: 'Bodega origen',
            accessorKey: 'bodega_origen_nombre',
            size: 75,
            Cell: ({ row }) => emptyCellObject(row.original?.bodega_origen_data, 'nombre')
          },
          {
            id: 'ubicacion_origen_nombre',
            header: 'Ubicación origen',
            accessorKey: 'ubicacion_origen_nombre',
            size: 75,
            Cell: ({ row }) => emptyCellObject(row.original?.ubicacion_origen_data, 'nombre')
          },
          {
            header: 'Bodega destino',
            accessorKey: 'bodega_destino_nombre',
            size: 75,
            Cell: ({ row }) => emptyCellObject(row.original?.bodega_destino_data, 'nombre')
          },
          {
            id: 'ubicacion_destino_nombre',
            header: 'Ubicación destino',
            accessorKey: 'ubicacion_destino_nombre',
            size: 75,
            Cell: ({ row }) => emptyCellObject(row.original?.ubicacion_destino_data, 'nombre')
          },

        ]
      }
    ],
    [],
  );

  const initDownload = useExcelDownloadLoaderStore(s => s.initDownload)

  const downloadKardexExcel = () => {
    const params = {
      ...form.getValues(),
      ...cleanedFilters,
    }
    initDownload('movimiento-inventario/excel-async',params)
  }

  return {
    refetch,
    downloadKardexExcel,
    form,
    setEnabled,
    enabled,
    bodega,
    flatpickrRef,
    setFilterObject,
    columnBase,
    dataKardex,
    isLoadingKardex,
    isRefetchingKardex,
    pagination,
    setPagination,
    columnFilters,
    setColumnFilters
  }

}
